<template>
    <section>
      <div
        class="px-4 mx-auto max-w-3xl lg:px-0"
        :class="[
          paddingTop === 'reduced' ? 'pt-6 lg:pt-6' : 'pt-3 lg:pt-6',
          paddingBottom === 'reduced' ? 'pb-6 lg:pb-6' : 'pb-6 lg:pb-12'
        ]"
      >
        <div class="flex flex-col gap-4 w-full">
          <div class="flex gap-3 items-center md:gap-5">
            <div class="relative shrink-0 size-9">
              <span
                class="absolute inset-0 rounded-full red-flag-badge-pulse bg-lifeboost-red"
                aria-hidden="true"
              />
              <div class="flex relative z-10 justify-center items-center text-base leading-7 text-white rounded-full size-9 bg-lifeboost-red font-ww-regular">
                {{ badgeNumber }}
              </div>
            </div>
            <span
              class="text-xs tracking-wider leading-5 uppercase text-[#F1574B] font-ww-regular"
              v-html="badgeText"
            />
          </div>
  
          <div class="pt-1.5">
            <h2
              class="text-2xl font-bold leading-tight sm:text-3xl lg:text-34 lg:leading-42 font-ww-bold text-[#3B2314]"
              v-html="title"
            />
          </div>
  
          <div
            class="pb-4 pt-2 text-17 leading-6 text-[#4A4A4A] font-ww-regular [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:space-y-0 [&_p+p]:mt-5"
            v-html="introContent"
          />
  
          <div
            v-if="calloutTitle || calloutContent"
            class="relative overflow-hidden rounded-b-lg border border-lifeboost-red border-t-[3px] bg-[#F0E8D8] px-7 pt-8 pb-6"
          >
            <nuxt-img
              v-if="stainImageSrc"
              :src="stainImageSrc"
              alt=""
              width="212"
              height="222"
              format="webp"
              loading="lazy"
              sizes="212px"
              class="absolute -top-1 -right-px w-[173px] h-[222px] mix-blend-multiply pointer-events-none"
              aria-hidden="true"
            />
  
            <div class="flex relative z-10 flex-col gap-6 lg:flex-row lg:gap-11 lg:items-center">
              <div
                class="flex-1 min-w-0"
                :class="stainImageSrc ? '' : ''"
              >
                <h3
                  v-if="calloutTitle"
                  class="flex gap-2.5 items-start text-xl font-bold leading-30 text-[#3B2314] font-ww-bold"
                >
                  <svg
                    class="shrink-0 mt-0.5 size-6 text-[#3B2314]"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path d="M12 2 1 21h22L12 2zm1 14h-2v-2h2v2zm0-4h-2V8h2v4z" />
                  </svg>
                  <span class="-mt-0.5" v-html="calloutTitle" />
                </h3>
  
                <div
                  v-if="calloutContent"
                  class="mt-6 text-15 leading-26 text-[#4A4A4A] font-ww-regular [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:space-y-0 [&_strong]:font-ww-bold [&_p+p]:mt-4 [&_sup]:text-xs"
                  v-html="calloutContent"
                />
              </div>
  
              <div
                v-if="calloutAsideImageSrc"
                class="mx-auto shrink-0 lg:mx-0"
              >
                <nuxt-img
                  :src="calloutAsideImageSrc"
                  :alt="calloutAsideImageAlt"
                  width="173"
                  height="285"
                  format="webp"
                  loading="lazy"
                  sizes="xs:173px sm:173px lg:173px"
                  class="object-contain w-full h-auto max-w-[173px]"
                />
              </div>
            </div>
          </div>
  
          <div
            v-if="outroContent"
            class="pt-3 text-17 leading-6 text-[#4A4A4A] font-ww-regular [&_p+p]:mt-5 [&_sup]:text-xs"
            v-html="outroContent"
          />
  
          <div
            v-if="showDivider"
            class="mt-10 h-px w-full bg-[#E0D5C5]"
            aria-hidden="true"
          />
        </div>
      </div>
    </section>
  </template>
  
<script setup>
defineProps({
  badgeNumber: {
      type: String,
      default: '3'
    },
    badgeText: {
      type: String,
      default: 'Red Flag No. 3'
    },
    title: {
      type: String,
      default: 'Inflammation Starts in the Gut — Men Pay the Price First'
    },
    introContent: {
      type: String,
      default: `<p>Most men don't realize how much their gut influences their entire body.</p>
  <p>A compromised gut can lead to:</p>
  <ul>
    <li>Chronic inflammation</li>
    <li>Low energy</li>
    <li>Poor nutrient absorption</li>
    <li>Brain fog</li>
    <li>Hormonal imbalance</li>
    <li>Digestive discomfort</li>
  </ul>
  <p>And here's the kicker:</p>`
    },
    stainImageSrc: {
      type: String,
      default: '/assets/images/red-flag-gut-stain.png'
    },
    calloutTitle: {
      type: String,
      default: 'Most Conventional Coffee Is Gut-Hostile'
    },
    calloutContent: {
      type: String,
      default: `<p><strong>Most conventional coffee is highly acidic (pH 4.8–5.1), which can irritate the gut.</strong><sup>19</sup></p>
  <p>Lifeboost's low-acid coffee?</p>
  <ul>
    <li><strong>Light roasts average pH 5.1–5.8.</strong></li>
    <li><strong>Dark roasts average pH 6.5–6.8 (nearly as alkaline as water).</strong></li>
  </ul>
  <p>That means smoother digestion, less irritation, and a gut-friendly daily ritual.</p>
  <p>Pair that with probiotics and sodium butyrate, a short-chain fatty acid shown to reduce inflammation and support gut lining integrity, and you have a powerful foundation for gut and immune health.</p>`
    },
    calloutAsideImageSrc: {
      type: String,
      default: '/assets/images/red-flag-gut-ph-scale.png'
    },
    calloutAsideImageAlt: {
      type: String,
      default: 'Coffee pH scale infographic comparing light and dark roast acidity'
    },
    outroContent: {
      type: String,
      default: ''
    },
    showDivider: {
      type: Boolean,
      default: false
    },
    paddingBottom: {
      type: String,
      default: 'reduced',
      validator: (value) => ['default', 'reduced'].includes(value)
    },
  paddingTop: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'reduced'].includes(value)
  }
})

defineBuilderComponent({
  friendlyName: 'Content - Red Flag Gut Image',
  inputs: [
    {
      name: 'badgeNumber',
      type: 'text',
      defaultValue: '3'
    },
    {
      name: 'badgeText',
      type: 'text',
      defaultValue: 'Red Flag No. 3'
    },
    {
      name: 'title',
      type: 'richText',
      defaultValue: 'Inflammation Starts in the Gut — Men Pay the Price First'
    },
    {
      name: 'introContent',
      type: 'richText',
      defaultValue: `<p>Most men don't realize how much their gut influences their entire body.</p>
<p>A compromised gut can lead to:</p>
<ul>
  <li>Chronic inflammation</li>
  <li>Low energy</li>
  <li>Poor nutrient absorption</li>
  <li>Brain fog</li>
  <li>Hormonal imbalance</li>
  <li>Digestive discomfort</li>
</ul>
<p>And here's the kicker:</p>`
    },
    {
      name: 'stainImageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
      defaultValue: '/assets/images/red-flag-gut-stain.png'
    },
    {
      name: 'calloutTitle',
      type: 'text',
      defaultValue: 'Most Conventional Coffee Is Gut-Hostile'
    },
    {
      name: 'calloutContent',
      type: 'richText',
      defaultValue: `<p><strong>Most conventional coffee is highly acidic (pH 4.8–5.1), which can irritate the gut.</strong><sup>19</sup></p>
<p>Lifeboost's low-acid coffee?</p>
<ul>
  <li><strong>Light roasts average pH 5.1–5.8.</strong></li>
  <li><strong>Dark roasts average pH 6.5–6.8 (nearly as alkaline as water).</strong></li>
</ul>
<p>That means smoother digestion, less irritation, and a gut-friendly daily ritual.</p>
<p>Pair that with probiotics and sodium butyrate, a short-chain fatty acid shown to reduce inflammation and support gut lining integrity, and you have a powerful foundation for gut and immune health.</p>`
    },
    {
      name: 'calloutAsideImageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
      defaultValue: '/assets/images/red-flag-gut-ph-scale.png'
    },
    {
      name: 'calloutAsideImageAlt',
      type: 'text',
      defaultValue: 'Coffee pH scale infographic comparing light and dark roast acidity'
    },
    {
      name: 'outroContent',
      type: 'richText',
      defaultValue: ''
    },
    {
      name: 'showDivider',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'paddingBottom',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'reduced'
    },
    {
      name: 'paddingTop',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'default'
    }
  ]
})
</script>